import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import apiClient from "../../services/apiClient";
import { 
  Heart, 
  BookOpen, 
  Volume2, 
  CheckCircle,
  Download,
  Youtube
} from "lucide-react";

export const ModuleGrid = () => {
  const [modules, setModules] = useState([]);
  const [userProgress, setUserProgress] = useState([]);
  const [dashboardText, setDashboardText] = useState('');
  const [dashboardImage, setDashboardImage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchModules();
    fetchUserProgress();
    fetchDashboardText();
    fetchDashboardImage();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await apiClient.get('/user/modules');
      setModules(response.data);
    } catch (error) {
      console.error('Failed to fetch modules:', error);
      toast.error('Failed to fetch modules');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProgress = async () => {
    try {
      const response = await apiClient.get('/user/progress');
      setUserProgress(response.data);
    } catch (error) {
      console.error('Failed to fetch user progress:', error);
    }
  };

  const fetchDashboardText = async () => {
    try {
      const response = await apiClient.get('/settings/dashboard-text');
      setDashboardText(response.data.text || '');
    } catch (error) {
      console.error('Failed to fetch dashboard text:', error);
    }
  };

  const fetchDashboardImage = async () => {
    try {
      const response = await apiClient.get('/settings/dashboard-image');
      setDashboardImage(response.data.filename || '');
    } catch (error) {
      console.error('Failed to fetch dashboard image:', error);
    }
  };

  const isModuleCompleted = (moduleId) => {
    return userProgress.some(progress => progress.module_id === moduleId);
  };

  // Count unique completed modules
  const getUniqueCompletedCount = () => {
    const uniqueModuleIds = new Set(userProgress.map(p => p.module_id));
    // Only count modules that still exist
    return modules.filter(m => uniqueModuleIds.has(m.id)).length;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 fade-in pb-16">
      {/* Logo and Motto */}
      <div className="text-center">
        <div className="flex justify-center mb-2">
          <img 
            src="/useba-logo-new.png" 
            alt="uSeba Logo" 
            className="w-40 sm:w-48 h-auto object-contain"
          />
        </div>
        
        {/* Dashboard Text and Image */}
        {(dashboardText || dashboardImage) && (
          <div className="mt-6 max-w-3xl mx-auto px-4">
            <div className={`flex ${dashboardImage ? 'flex-col sm:flex-row' : 'flex-col'} items-center gap-4 sm:gap-6`}>
              {dashboardText && (
                <p className={`text-charcoal-600 text-sm sm:text-base ${dashboardImage ? 'sm:text-left text-center sm:flex-1' : 'text-center'}`}>
                  {dashboardText}
                </p>
              )}
              {dashboardImage && (
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/images/${dashboardImage}`}
                  alt="Dashboard"
                  className="w-full sm:w-48 max-w-xs h-auto rounded-lg shadow-md object-cover"
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 sm:gap-6 stats-grid">
        <div className="card p-3 sm:p-6 text-center flex flex-col items-center justify-center">
          <BookOpen className="w-5 sm:w-8 h-5 sm:h-8 text-sage-500 mb-1 sm:mb-2" />
          <h3 className="text-lg sm:text-2xl font-bold text-charcoal-800">{modules.length}</h3>
          <p className="text-charcoal-600 text-xs sm:text-base text-center leading-tight">Dostupné<br className="sm:hidden" /> moduly</p>
        </div>
        <div className="card p-3 sm:p-6 text-center flex flex-col items-center justify-center">
          <CheckCircle className="w-5 sm:w-8 h-5 sm:h-8 text-green-500 mb-1 sm:mb-2" />
          <h3 className="text-lg sm:text-2xl font-bold text-charcoal-800">{getUniqueCompletedCount()}</h3>
          <p className="text-charcoal-600 text-xs sm:text-base text-center leading-tight">Hotové</p>
        </div>
        <div className="card p-3 sm:p-6 text-center flex flex-col items-center justify-center">
          <Heart className="w-5 sm:w-8 h-5 sm:h-8 text-pink-500 mb-1 sm:mb-2" />
          <h3 className="text-lg sm:text-2xl font-bold text-charcoal-800">
            {Math.max(0, modules.length - getUniqueCompletedCount())}
          </h3>
          <p className="text-charcoal-600 text-xs sm:text-base text-center leading-tight">Ešte<br className="sm:hidden" /> neobjavené</p>
        </div>
      </div>

      {/* Dashboard Text */}
      {/* Modules Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 module-grid">
        {modules.map((module) => (
          <Link
            key={module.id}
            to={`/portal/module/${module.id}`}
            className="card p-4 hover:shadow-lg transition-all duration-300 group relative flex"
          >
            {/* Completion Badge */}
            {isModuleCompleted(module.id) && (
              <div className="absolute top-3 right-3 z-10">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
            )}

            {/* Image */}
            <div className="w-24 sm:w-32 h-24 sm:h-32 flex-shrink-0 mr-4">
              {module.image_filename ? (
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/images/${module.image_filename}`}
                  alt={module.title}
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <div className="w-full h-full bg-sage-100 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-8 h-8 text-sage-400" />
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col justify-between min-w-0">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-charcoal-800 mb-1 font-display truncate pr-6">
                  {module.title}
                </h3>
                <p className="text-charcoal-600 text-sm line-clamp-2">
                  {module.description}
                </p>
              </div>

              {/* Features */}
              <div className="flex items-center justify-between text-xs sm:text-sm text-charcoal-500 mt-2">
                <div className="flex items-center space-x-2 sm:space-x-3 flex-wrap">
                  {module.audio_filename && (
                    <span className="flex items-center text-blue-600">
                      <Volume2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      Audio
                    </span>
                  )}
                  {module.youtube_url && (
                    <span className="flex items-center text-red-600">
                      <Youtube className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      Video
                    </span>
                  )}
                  {module.download_filename && (
                    <span className="flex items-center text-green-600">
                      <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      PDF
                    </span>
                  )}
                </div>
                <span className="text-sage-600 font-medium group-hover:text-sage-700 hidden sm:inline">
                  Začať →
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {modules.length === 0 && (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-sage-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-charcoal-600 mb-2">No modules available</h3>
          <p className="text-charcoal-500">Check back later for new wellness content</p>
        </div>
      )}
    </div>
  );
};
